import { FISH_KINDS, FISH_GAME, FISH_SIZES, FISH_RARITY, FISH_RARITY_COLORS, FISH_RARITY_ODDS, FISH_RARITIES, FISHING_LEVEL_CAP, FISH_ZONE_W, ITEM_NAMES, fishXpFor, fishingXpForLevel, type FishKind } from "@ghostland/shared";
import { statsStore } from "../items/statsStore.js";

/**
 * Fishing DEBUG panel (dev builds; toggled with ~ or `). Live settings the FishingController reads:
 *  - `enabled`   → the bite wait drops to 0 (the fish bites the moment the line lands)
 *  - `forceKind` → the next bite is always this fish (instead of the level-gated roll)
 * The panel also lists every fish's tuning stats (unlock/XP/speed/energy/size range).
 * NOTE: the server still validates catches (rod, water range, fishing level) — forcing a fish
 * above your level makes the minigame run but the server will refuse the item.
 */
export const fishDebug: { enabled: boolean; forceKind: FishKind | null; zoneMin: number; zoneMax: number } = {
  enabled: false,
  forceKind: null,
  zoneMin: FISH_ZONE_W.min, // green-zone width at fishing level 1
  zoneMax: FISH_ZONE_W.max, // green-zone width at level 60+
};

/** Wired by world3d: sends the level change to the server (admin / non-production only). */
export interface FishingDebugActions { setLevel(level: number): void; }
let actions: FishingDebugActions | null = null;
export function setFishingDebugActions(a: FishingDebugActions): void { actions = a; }

let panel: HTMLDivElement | null = null;

export function isFishingDebugOpen(): boolean {
  return !!panel && panel.style.display !== "none";
}

export function toggleFishingDebug(): void {
  if (!panel) { panel = buildPanel(); panel.style.display = "block"; return; } // first open: just show
  panel.style.display = isFishingDebugOpen() ? "none" : "block";
}

function buildPanel(): HTMLDivElement {
  const el = document.createElement("div");
  el.id = "gl-fish-debug";
  el.style.cssText = [
    "position:fixed", "top:72px", "right:16px", "z-index:60", "width:280px",
    "background:#0a0a0a", "border:2px solid #f4f4f4", "box-shadow:0 0 0 2px #000",
    "color:#f4f4f4", "font-family:ui-monospace,monospace", "font-size:11px",
    "padding:10px 12px", "image-rendering:pixelated", "user-select:none",
    "max-height:calc(100vh - 100px)", "overflow-y:auto",
  ].join(";");

  const title = document.createElement("div");
  title.textContent = "FISHING DEBUG";
  title.style.cssText = "font-size:12px;letter-spacing:2px;margin-bottom:8px;color:#7ce0a0";
  el.appendChild(title);

  // ── debug mode: zero bite wait ────────────────────────────────────────────
  const modeRow = document.createElement("label");
  modeRow.style.cssText = "display:flex;align-items:center;gap:8px;cursor:pointer;margin-bottom:8px";
  const cb = document.createElement("input");
  cb.type = "checkbox";
  cb.checked = fishDebug.enabled;
  cb.onchange = () => { fishDebug.enabled = cb.checked; };
  const cbText = document.createElement("span");
  cbText.textContent = "Debug mode (bite wait = 0)";
  modeRow.append(cb, cbText);
  el.appendChild(modeRow);

  // ── forced fish kind ──────────────────────────────────────────────────────
  const kindLabel = document.createElement("div");
  kindLabel.textContent = "Trigger fish";
  kindLabel.style.cssText = "letter-spacing:1px;color:#9aa4ae;margin-bottom:4px";
  el.appendChild(kindLabel);
  const kinds = document.createElement("div");
  kinds.style.cssText = "display:flex;gap:4px;flex-wrap:wrap;margin-bottom:10px";
  const buttons: HTMLButtonElement[] = [];
  const mkBtn = (label: string, kind: FishKind | null): HTMLButtonElement => {
    const b = document.createElement("button");
    b.textContent = label;
    b.style.cssText = "background:#0a0a0a;color:#f4f4f4;border:1px solid #f4f4f4;padding:3px 7px;font:inherit;cursor:pointer";
    b.onclick = () => {
      fishDebug.forceKind = kind;
      for (const o of buttons) { o.style.background = "#0a0a0a"; o.style.color = "#f4f4f4"; }
      b.style.background = "#7ce0a0"; b.style.color = "#0a0a0a";
    };
    buttons.push(b);
    kinds.appendChild(b);
    return b;
  };
  const natural = mkBtn("Natural", null);
  for (const k of FISH_KINDS) mkBtn(ITEM_NAMES[k] ?? k, k);
  natural.style.background = "#7ce0a0"; natural.style.color = "#0a0a0a"; // default selection
  el.appendChild(kinds);

  // ── fishing level adjuster ────────────────────────────────────────────────
  const lvlLabel = document.createElement("div");
  lvlLabel.style.cssText = "letter-spacing:1px;color:#9aa4ae;margin-bottom:4px";
  const renderLvl = (): void => { lvlLabel.textContent = `Fishing level (current: ${statsStore.get().fishing.level})`; };
  renderLvl();
  statsStore.subscribe(renderLvl);
  el.appendChild(lvlLabel);
  const lvlRow = document.createElement("div");
  lvlRow.style.cssText = "display:flex;gap:4px;margin-bottom:10px";
  const lvlInput = document.createElement("input");
  lvlInput.type = "number";
  lvlInput.min = "1"; lvlInput.max = String(FISHING_LEVEL_CAP);
  lvlInput.value = String(statsStore.get().fishing.level);
  lvlInput.style.cssText = "width:64px;background:#0a0a0a;color:#f4f4f4;border:1px solid #f4f4f4;padding:3px 6px;font:inherit";
  const lvlBtn = document.createElement("button");
  lvlBtn.textContent = "Set";
  lvlBtn.style.cssText = "background:#0a0a0a;color:#f4f4f4;border:1px solid #f4f4f4;padding:3px 10px;font:inherit;cursor:pointer";
  lvlBtn.onclick = () => {
    const lv = Math.min(FISHING_LEVEL_CAP, Math.max(1, Math.floor(Number(lvlInput.value) || 1)));
    lvlInput.value = String(lv);
    actions?.setLevel(lv);
  };
  lvlRow.append(lvlInput, lvlBtn);
  el.appendChild(lvlRow);

  // ── green-zone width tuning (applies live, even mid-fight) ────────────────
  const zoneLabel = document.createElement("div");
  zoneLabel.textContent = `Zone width: min (Lv1) / max (Lv${FISH_ZONE_W.maxLevel})`;
  zoneLabel.style.cssText = "letter-spacing:1px;color:#9aa4ae;margin-bottom:4px";
  el.appendChild(zoneLabel);
  const zoneRow = document.createElement("div");
  zoneRow.style.cssText = "display:flex;gap:4px;margin-bottom:10px";
  const zInput = (value: number, apply: (v: number) => void): HTMLInputElement => {
    const i = document.createElement("input");
    i.type = "number";
    i.min = "0.02"; i.max = "0.6"; i.step = "0.01";
    i.value = String(value);
    i.style.cssText = "width:64px;background:#0a0a0a;color:#f4f4f4;border:1px solid #f4f4f4;padding:3px 6px;font:inherit";
    i.oninput = () => { const v = Number(i.value); if (Number.isFinite(v) && v > 0) apply(Math.min(0.6, Math.max(0.02, v))); };
    return i;
  };
  zoneRow.append(
    zInput(fishDebug.zoneMin, (v) => { fishDebug.zoneMin = v; }),
    zInput(fishDebug.zoneMax, (v) => { fishDebug.zoneMax = v; }),
  );
  el.appendChild(zoneRow);

  // ── rarity odds by level bracket ──────────────────────────────────────────
  const oddsLabel = document.createElement("div");
  oddsLabel.textContent = "Rarity odds by level";
  oddsLabel.style.cssText = "letter-spacing:1px;color:#9aa4ae;margin-bottom:4px";
  el.appendChild(oddsLabel);
  const oddsWrap = document.createElement("div");
  oddsWrap.style.cssText = "border:1px solid #3a444e;margin-bottom:10px";
  let oddsRows = `<tr><th style="text-align:left;padding:2px 6px">Lv</th>` +
    FISH_RARITIES.map((r) => `<th style="text-align:right;padding:2px 6px;color:${FISH_RARITY_COLORS[r]}">${r[0].toUpperCase()}</th>`).join("") + "</tr>";
  for (let bi = 0; bi < FISH_RARITY_ODDS.length; bi++) {
    const b = FISH_RARITY_ODDS[bi];
    const to = FISH_RARITY_ODDS[bi + 1] ? FISH_RARITY_ODDS[bi + 1].minLevel - 1 : FISHING_LEVEL_CAP;
    oddsRows += `<tr><td style="padding:1px 6px">${b.minLevel}–${to}</td>` +
      FISH_RARITIES.map((r) => `<td style="text-align:right;padding:1px 6px;color:#c7cfd6">${b.odds[r]}%</td>`).join("") + "</tr>";
  }
  const oddsTable = document.createElement("table");
  oddsTable.style.cssText = "border-collapse:collapse;width:100%;font-size:10px";
  oddsTable.innerHTML = oddsRows;
  oddsWrap.appendChild(oddsTable);
  el.appendChild(oddsWrap);

  // ── fish stats table ──────────────────────────────────────────────────────
  const statsLabel = document.createElement("div");
  statsLabel.textContent = "Fish stats";
  statsLabel.style.cssText = "letter-spacing:1px;color:#9aa4ae;margin-bottom:4px";
  el.appendChild(statsLabel);
  for (const k of FISH_KINDS) {
    const g = FISH_GAME[k], sz = FISH_SIZES[k];
    const rarity = FISH_RARITY[k], rc = FISH_RARITY_COLORS[rarity];
    const card = document.createElement("div");
    card.style.cssText = "border:1px solid #3a444e;padding:5px 7px;margin-bottom:5px;line-height:1.5";
    card.innerHTML =
      `<div style="color:${rc}">${ITEM_NAMES[k] ?? k} <span style="float:right">${rarity.toUpperCase()}</span></div>` +
      `<div>${fishXpFor(k)} XP (halves when outgrown)</div>` +
      `<div>Speed ${g.speed.toFixed(2)} &nbsp;·&nbsp; Energy ${g.energy.toFixed(2)}</div>` +
      `<div>Size ${sz.min}&#8243;–${sz.max}&#8243;</div>`;
    el.appendChild(card);
  }

  // ── XP curve: XP to next level + catches of each fish to level up ─────────
  const xpLabel = document.createElement("div");
  xpLabel.textContent = "XP per level (catches to level up)";
  xpLabel.style.cssText = "letter-spacing:1px;color:#9aa4ae;margin-bottom:4px";
  el.appendChild(xpLabel);
  const xpWrap = document.createElement("div");
  xpWrap.style.cssText = "max-height:180px;overflow-y:auto;border:1px solid #3a444e;margin-bottom:5px";
  const short: Record<FishKind, string> = {
    mudfish: "Mud", pondfish: "Pond", bog_carp: "Carp", moss_perch: "Perch", sun_carp: "Sun", ghost_koi: "Koi",
  };
  let rows = `<tr><th style="text-align:left;padding:2px 6px">Lv</th><th style="text-align:right;padding:2px 6px">XP</th>`;
  for (const k of FISH_KINDS) rows += `<th style="text-align:right;padding:2px 6px;color:${FISH_RARITY_COLORS[FISH_RARITY[k]]}">${short[k]}</th>`;
  rows += "</tr>";
  for (let lv = 1; lv < FISHING_LEVEL_CAP; lv++) {
    const need = fishingXpForLevel(lv);
    rows += `<tr><td style="padding:1px 6px">${lv}</td><td style="text-align:right;padding:1px 6px">${need.toLocaleString()}</td>`;
    for (const k of FISH_KINDS) {
      // catches to clear this level with ONLY this fish (— before its unlock level)
      const n = String(Math.ceil(need / fishXpFor(k, lv)));
      rows += `<td style="text-align:right;padding:1px 6px;color:#c7cfd6">${n}</td>`;
    }
    rows += "</tr>";
  }
  const xpTable = document.createElement("table");
  xpTable.style.cssText = "border-collapse:collapse;width:100%;font-size:10px";
  xpTable.innerHTML = rows;
  xpWrap.appendChild(xpTable);
  el.appendChild(xpWrap);

  const hint = document.createElement("div");
  hint.textContent = "~ to close";
  hint.style.cssText = "color:#9aa4ae;margin-top:4px;letter-spacing:1px";
  el.appendChild(hint);

  document.body.appendChild(el);
  return el;
}
